import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  catchError,
  finalize,
  Observable,
  shareReplay,
  switchMap,
  throwError,
} from 'rxjs';

import { API } from '../api/api';
import { Auth } from '../services/auth/auth-service';
import { LoadingService } from '../services/loading/loading-service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private refreshRequest$: Observable<string> | null = null;

  constructor(
    private auth: Auth,
    private loader: LoadingService,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = this.auth.getAccessToken();
    const authRequest = token
      ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      : req;

    this.loader.show();

    return next.handle(authRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status !== 401) {
          return throwError(() => error);
        }

        const url = req.url.toLowerCase();
        const isAuthEndpoint = url.includes('/auth/');
        const isRefreshRequest = url.includes(API.AUTH.REFRESH_TOKEN.toLowerCase());

        // Auth endpoint errors (including refresh) are handled by their caller.
        // A failed refresh is handled by the shared refresh observable below.
        if (isAuthEndpoint || isRefreshRequest) {
          return throwError(() => error);
        }

        if (!this.refreshRequest$) {
          this.refreshRequest$ = this.auth.refreshAccessToken().pipe(
            switchMap((response) => {
              if (!response.accessToken) {
                return throwError(() => new Error('Refresh response did not contain an access token'));
              }
              return [response.accessToken];
            }),
            catchError((refreshError) => {
              this.auth.handleRefreshFailure();
              return throwError(() => refreshError);
            }),
            finalize(() => {
              this.refreshRequest$ = null;
            }),
            shareReplay({ bufferSize: 1, refCount: false }),
          );
        }

        return this.refreshRequest$.pipe(
          switchMap((newToken) =>
            next.handle(
              req.clone({ setHeaders: { Authorization: `Bearer ${newToken}` } }),
            ),
          ),
        );
      }),
      finalize(() => this.loader.hide()),
    );
  }
}
